import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import {
  Bell,
  Heart,
  MessageCircle,
  Check,
  Clock,
  Loader2,
  ArrowLeft,
  CheckCheck,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';

export default function NotificationsPage() {
  const { api, requestPushPermission } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPushBanner, setShowPushBanner] = useState(
    typeof Notification !== 'undefined' && Notification.permission !== 'granted'
  );
  const [pushBlocked, setPushBlocked] = useState(
    typeof Notification !== 'undefined' && Notification.permission === 'denied'
  );

  const handleEnablePush = async () => {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission === 'denied') {
      setPushBlocked(true);
      return;
    }
    const granted = await requestPushPermission();
    if (granted) {
      setShowPushBanner(false);
      setPushBlocked(false);
    } else {
      // Permission denied after prompt, or already denied
      setPushBlocked(Notification.permission === 'denied');
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await api.get('/notifications/');
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/read`);
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.post('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleNotificationClick = (notif) => {
    if (!notif.is_read) markAsRead(notif.id);

    const preAuthTypes = ['preauth_requested', 'preauth_captured', 'preauth_released'];
    if (preAuthTypes.includes(notif.type) && notif.data?.match_id) {
      // Security hold notifications → go to chat thread
      navigate(`/messages/${notif.data.match_id}`);
    } else if (notif.type === 'counter_offer' && notif.data?.match_id) {
      // Counter-offer → go to chat thread
      navigate(`/messages/${notif.data.match_id}`);
    } else if (notif.data?.match_id) {
      navigate(`/matches/${notif.data.match_id}`);
    } else if (notif.data?.item_id && ['new_offer', 'offer_declined', 'offer_expired', 'offer_withdrawn', 'offer_accepted', 'counter_offer'].includes(notif.type)) {
      navigate(`/catalog/${notif.data.item_id}`);
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'match_found':
        return <Heart className="w-5 h-5 text-[#C9956C]" />;
      case 'message':
        return <MessageCircle className="w-5 h-5 text-[var(--theme-text-primary)]" />;
      case 'match_unlocked':
        return <Check className="w-5 h-5 text-[#059669]" />;
      case 'new_offer':
      case 'counter_offer':
        return <Check className="w-5 h-5 text-[#C9956C]" />;
      case 'offer_accepted':
        return <Check className="w-5 h-5 text-[#059669]" />;
      case 'offer_declined':
      case 'offer_expired':
      case 'offer_withdrawn':
        return <Clock className="w-5 h-5 text-[var(--theme-text-muted)]" />;
      default:
        return <Bell className="w-5 h-5 text-[var(--theme-text-secondary)]" />;
    }
  };

  const getIconBg = (type) => {
    switch (type) {
      case 'match_found':
        return 'bg-[#C9956C]/10';
      case 'message':
        return 'bg-[var(--theme-hover)]';
      case 'match_unlocked':
        return 'bg-[#059669]/10';
      case 'new_offer':
      case 'counter_offer':
        return 'bg-[#C9956C]/10';
      case 'offer_accepted':
        return 'bg-[#059669]/10';
      default:
        return 'bg-[var(--theme-hover)]';
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  if (loading) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 py-12 flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[var(--theme-text-primary)]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center gap-2 text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)] mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-[#C9956C]/10 flex items-center justify-center">
                <Bell className="w-6 h-6 text-[#C9956C]" />
              </div>
              <div>
                <h1 className="text-2xl font-black text-[var(--theme-text-primary)]">Notifications</h1>
                <p className="text-sm text-[var(--theme-text-secondary)]">
                  {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up!'}
                </p>
              </div>
            </div>
            {unreadCount > 0 && (
              <Button 
                variant="outline" 
                onClick={markAllAsRead}
                className="border-[var(--theme-border)] text-[var(--theme-text-secondary)] hover:bg-[var(--theme-hover)] rounded-full"
              >
                <CheckCheck className="w-4 h-4 mr-2" />
                Mark all read
              </Button>
            )}
          </div>
        </div>

        {/* Push permission banner */}
        {showPushBanner && (
          <div className={`mb-4 flex items-center justify-between gap-3 rounded-xl border px-4 py-3 ${
            pushBlocked
              ? 'border-red-300/30 bg-red-50 dark:bg-red-900/10'
              : 'border-[#C9956C]/30 bg-[#C9956C]/10'
          }`}>
            <div className="flex items-center gap-3">
              <Bell className={`w-5 h-5 flex-shrink-0 ${pushBlocked ? 'text-red-400' : 'text-[#C9956C]'}`} />
              <p className="text-sm text-[var(--theme-text-primary)]">
                {pushBlocked
                  ? 'Notifications are blocked. Go to Chrome \u2192 Site settings \u2192 Notifications \u2192 dev.wantd.au \u2192 Allow.'
                  : 'Get instant alerts when you receive matches, offers, and messages.'}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {!pushBlocked && (
                <Button
                  size="sm"
                  onClick={handleEnablePush}
                  className="rounded-full bg-[#C9956C] text-white hover:bg-[#b8845b] text-xs px-3"
                >
                  Enable
                </Button>
              )}
              <button
                onClick={() => setShowPushBanner(false)}
                className="text-[var(--theme-text-muted)] hover:text-[var(--theme-text-secondary)]"
                aria-label="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Notifications List */}
        {notifications.length > 0 ? (
          <div className="space-y-2">
            {notifications.map((notif) => (
              <button
                key={notif.id}
                onClick={() => handleNotificationClick(notif)}
                className={`w-full text-left p-4 rounded-xl border transition-all hover:shadow-md ${
                  notif.is_read 
                    ? 'bg-[var(--theme-card)] border-[var(--theme-border)]' 
                    : 'bg-[var(--theme-hover)] border-[#C9956C]/30'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${getIconBg(notif.type)}`}>
                    {getNotificationIcon(notif.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-sm ${!notif.is_read ? 'font-bold text-[var(--theme-text-primary)]' : 'font-medium text-[var(--theme-text-primary)]'}`}>
                        {notif.title}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-[var(--theme-text-muted)] flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatTimeAgo(notif.created_at)}
                        </span>
                        {!notif.is_read && (
                          <span className="w-2 h-2 bg-[#C9956C] rounded-full"></span>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-[var(--theme-text-secondary)]">{notif.message}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="bg-[var(--theme-card)] rounded-2xl border border-[var(--theme-border)] p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-[var(--theme-hover)] flex items-center justify-center mx-auto mb-4">
              <Bell className="w-8 h-8 text-[var(--theme-text-muted)]" />
            </div>
            <h3 className="text-xl font-bold text-[var(--theme-text-primary)] mb-2">No notifications yet</h3>
            <p className="text-[var(--theme-text-secondary)] max-w-sm mx-auto">
              When you receive matches, messages, or other updates, they will appear here.
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}
